/**
 * Peças comuns das páginas públicas da família — specs 0067 e 0071.
 *
 * `fetch` puro, não o cliente do painel: aqui 401 é "link inválido", não sessão
 * expirada. O token vai sempre em header, nunca no caminho da chamada, que iria
 * para o log de acesso.
 */

import React from 'react';

export class ErroDaPagina extends Error {
  constructor(readonly codigo: string, mensagem: string) { super(mensagem); }
}

export type Chamar = <T>(caminho: string, init?: RequestInit) => Promise<T>;

/** Um cliente para uma base da API, com os headers de credencial dela. */
export function criarChamada(base: string, headers: Record<string, string>): Chamar {
  return async <T,>(caminho: string, init?: RequestInit): Promise<T> => {
    let resposta: Response;
    try {
      resposta = await fetch(`${base}${caminho}`, {
        ...init,
        headers: { ...(init?.body instanceof FormData ? {} : { 'Content-Type': 'application/json' }), ...headers, ...init?.headers },
      });
    } catch {
      throw new ErroDaPagina('REDE', 'Sem conexão. Confira a internet e tente de novo.');
    }
    const corpo = await resposta.json().catch(() => ({}));
    if (!resposta.ok) throw new ErroDaPagina(corpo.codigo ?? 'ERRO', corpo.error ?? 'Não foi possível concluir agora. Tente de novo.');
    return corpo.data as T;
  };
}

export function hora(iso: string): string {
  return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Sao_Paulo' });
}

/** "2026-09-24" → "qui, 24/09". Meio-dia, para o fuso nunca empurrar o dia. */
export function diaCurto(dataIso: string): string {
  const d = new Date(`${dataIso.slice(0, 10)}T12:00:00-03:00`);
  const semana = d.toLocaleDateString('pt-BR', { weekday: 'short', timeZone: 'America/Sao_Paulo' }).replace('.', '');
  return `${semana}, ${d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', timeZone: 'America/Sao_Paulo' })}`;
}

/** Hoje e amanhã no fuso de Brasília, como AAAA-MM-DD. */
export function hojeBRT(deslocamentoDias = 0): string {
  const d = new Date(Date.now() + deslocamentoDias * 86_400_000);
  return d.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
}

export const Casca: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="fam-pagina">
    <header className="fam-topo">
      <span className="fam-marca">Coopertransmig</span>
      <span className="fam-marca-apoio">Transporte escolar</span>
    </header>
    <main className="fam-conteudo">{children}</main>
  </div>
);

/** Rótulo, ajuda e erro ligados ao campo — o erro fica embaixo de quem errou. */
export const Campo: React.FC<{
  id: string;
  rotulo: string;
  ajuda?: React.ReactNode;
  erro?: string;
  children: (a11y: { id: string; 'aria-invalid': boolean; 'aria-describedby'?: string }) => React.ReactNode;
}> = ({ id, rotulo, ajuda, erro, children }) => {
  const descritores = [ajuda ? `${id}-ajuda` : null, erro ? `${id}-erro` : null].filter(Boolean).join(' ');
  return (
    <div className="fam-campo">
      <label className="fam-rotulo" htmlFor={id}>{rotulo}</label>
      {children({ id, 'aria-invalid': Boolean(erro), 'aria-describedby': descritores || undefined })}
      {ajuda && <small id={`${id}-ajuda`} className="fam-ajuda">{ajuda}</small>}
      {erro && <small id={`${id}-erro`} className="fam-erro-campo">{erro}</small>}
    </div>
  );
};

/**
 * Escolha única em pílula: `<input type="radio">` nativo com `<label>`, a marcação
 * `toggle-pill` do painel, no tamanho de dedo desta página.
 */
export function Escolha<V extends string>({ nome, legenda, opcoes, valor, onChange, desabilitado }: {
  nome: string;
  legenda: string;
  opcoes: { valor: V; rotulo: string }[];
  valor: V;
  onChange: (v: V) => void;
  desabilitado?: boolean;
}): React.ReactElement {
  return (
    <fieldset className="fam-escolha">
      <legend className="fam-rotulo">{legenda}</legend>
      <div className="toggle-pill fam-pilula">
        {opcoes.map((o) => (
          <React.Fragment key={o.valor}>
            <input type="radio" id={`${nome}-${o.valor}`} name={nome} value={o.valor} checked={valor === o.valor}
              onChange={() => onChange(o.valor)} disabled={desabilitado} />
            <label htmlFor={`${nome}-${o.valor}`}>{o.rotulo}</label>
          </React.Fragment>
        ))}
      </div>
    </fieldset>
  );
}
